import React, { useEffect, useState } from "react"
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  InternalIcon,
  Link,
  Typography
} from "@carrotfertility/carotene-core"
import { DialogActionsBar } from "@carrotfertility/carotene-core-x"
import { FormattedMessage } from "react-intl"
import { useHistory } from "react-router-dom"
import { AccessOriginAccountDialog } from "#/components/telemedicine/pelvic-floor-therapy/AccessOriginAccountDialog"
import { useTelemedicinePelvicFloorTherapyInterest } from "#/components/telemedicine/hooks/useTelemedicineInterest"
import useGetUserInfoConditionsSubset from "#/services/user/hooks/useGetCurrentUserConditionsSubset"
import { useButtonClickTracking } from "#/services/analytics/useButtonClickTracking"
import { Paths } from "#/utils/Paths"

function TelemedicineButton({
  isDisabled,
  onClick,
  children
}: {
  isDisabled: boolean
  onClick: () => void
  children: React.ReactNode
}): JSX.Element {
  return (
    <Box
      sx={(theme) => ({
        marginBlockStart: theme.spacing(theme.tokens.spacing.xs),
        marginBlockEnd: theme.spacing(theme.tokens.spacing.lg),
        ":last-child": { marginBlockEnd: 0 }
      })}
    >
      <Button onClick={onClick} disabled={isDisabled}>
        {children}
      </Button>
    </Box>
  )
}

function TelemedicineLink({
  isDisabled,
  onClick,
  children
}: {
  isDisabled: boolean
  onClick: () => void
  children: React.ReactNode
}): JSX.Element {
  if (isDisabled) {
    return null
  }
  return (
    <Box>
      <Link component="button" endIcon={<InternalIcon fontSize="small" color="primary" />} onClick={onClick}>
        {children}
      </Link>
    </Box>
  )
}

function TelemedicineForPelvicFloorTherapyInternal({
  variant,
  children
}: {
  variant: "button" | "link"
  children: React.ReactNode
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const { data } = useGetUserInfoConditionsSubset()
  const { trackButtonClick } = useButtonClickTracking()
  const isDisabled = data?.hasCarrotPlanOnTheWay ?? false

  const handleClick = () => {
    trackButtonClick({ name: "Access Origin account" })
    setOpen(true)
  }

  return (
    <>
      {variant === "button" ? (
        <TelemedicineButton isDisabled={isDisabled} onClick={handleClick}>
          {children}
        </TelemedicineButton>
      ) : (
        <TelemedicineLink isDisabled={isDisabled} onClick={handleClick}>
          {children}
        </TelemedicineLink>
      )}
      <AccessOriginAccountDialog open={open} onClose={() => setOpen(false)} />
    </>
  )
}

export function TelemedicineForPelvicFloorTherapyButton({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <TelemedicineForPelvicFloorTherapyInternal variant="button">{children}</TelemedicineForPelvicFloorTherapyInternal>
  )
}

export function TelemedicineForPelvicFloorTherapyLink({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <TelemedicineForPelvicFloorTherapyInternal variant="link">{children}</TelemedicineForPelvicFloorTherapyInternal>
  )
}

function ThanksForYourInterestDialog({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const { mutate: createTelemedicineInterest } = useTelemedicinePelvicFloorTherapyInterest()
  const history = useHistory()

  useEffect(() => {
    if (open) {
      createTelemedicineInterest()
    }
  }, [open, createTelemedicineInterest])

  const handleGoToCarrotHome = () => {
    onClose()
    history.push(Paths.HOME)
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <FormattedMessage defaultMessage="Thanks for your interest" />
      </DialogTitle>
      <DialogContent>
        <Typography>
          <FormattedMessage defaultMessage="We've noted your interest in pelvic floor physical therapy with Origin and will be in touch with any updates." />
        </Typography>
      </DialogContent>
      <DialogActionsBar
        primaryAction={
          <Button variant="outlined" color="secondary" onClick={handleGoToCarrotHome}>
            <FormattedMessage defaultMessage="Go to Carrot home" />
          </Button>
        }
      />
    </Dialog>
  )
}

export function PelvicFloorTherapyInterestLink({ children }: { children: React.ReactNode }): JSX.Element {
  const [open, setOpen] = useState(false)

  return (
    <Typography>
      <Link component="button" color="inherit" onClick={() => setOpen(true)} fontStyle="italic">
        {children}
      </Link>
      <ThanksForYourInterestDialog open={open} onClose={() => setOpen(false)} />
    </Typography>
  )
}
